import { useState } from 'react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Calendar, Plus, Trash2, Save } from 'lucide-react'

export interface EventItem {
  id: number
  event_date: string | null
  description: string | null
  position: number
}

interface EventsManagerProps {
  pageContentId: number
  initialEvents: EventItem[]
}

interface DraftEvent {
  id?: number
  event_date: string
  description: string
  position: number
  dirty: boolean
  saving: boolean
  error?: string
}

function showToast(message: string, success = true) {
  const div = document.createElement('div')
  div.className = `fixed top-4 right-4 ${success ? 'bg-green-500' : 'bg-red-500'} text-white px-4 py-2 rounded-lg shadow-lg z-50`
  div.textContent = message
  document.body.appendChild(div)
  setTimeout(() => div.remove(), 3000)
}

function getCsrfToken(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
  return meta?.content || ''
}

async function jsonFetch(url: string, init: RequestInit = {}) {
  const res = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
      ...(init.headers || {})
    },
    credentials: 'same-origin'
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(body?.errors ? JSON.stringify(body.errors) : 'Request failed')
  }
  return body
}

export function EventsManager({ pageContentId, initialEvents }: EventsManagerProps) {
  const [events, setEvents] = useState<DraftEvent[]>(
    initialEvents.map(e => ({
      id: e.id,
      event_date: e.event_date || '',
      description: e.description || '',
      position: e.position,
      dirty: false,
      saving: false
    }))
  )

  const updateEvent = (index: number, patch: Partial<DraftEvent>) => {
    setEvents(prev => prev.map((e, i) => (i === index ? { ...e, ...patch } : e)))
  }

  const addEvent = () => {
    setEvents(prev => [
      ...prev,
      {
        event_date: '',
        description: '',
        position: prev.length,
        dirty: true,
        saving: false
      }
    ])
  }

  const saveEvent = async (index: number) => {
    const event = events[index]
    if (!event.event_date || !event.description) {
      updateEvent(index, { error: 'Date et description requises' })
      return
    }
    updateEvent(index, { saving: true, error: undefined })

    try {
      if (event.id) {
        const body = await jsonFetch(`/admin/page_contents/${pageContentId}/events/${event.id}`, {
          method: 'PATCH',
          body: JSON.stringify({
            event: {
              event_date: event.event_date,
              description: event.description,
              position: event.position
            }
          })
        })
        updateEvent(index, { saving: false, dirty: false, id: body.event.id })
        showToast('Événement enregistré')
      } else {
        const body = await jsonFetch(`/admin/page_contents/${pageContentId}/events`, {
          method: 'POST',
          body: JSON.stringify({
            event: {
              event_date: event.event_date,
              description: event.description,
              position: event.position
            }
          })
        })
        updateEvent(index, { saving: false, dirty: false, id: body.event.id })
        showToast('Événement créé')
      }
    } catch (err) {
      updateEvent(index, { saving: false, error: (err as Error).message })
      showToast('Erreur lors de l\'enregistrement', false)
    }
  }

  const deleteEvent = async (index: number) => {
    const event = events[index]
    if (!event.id) {
      setEvents(prev => prev.filter((_, i) => i !== index))
      return
    }
    if (!confirm('Supprimer cet événement ?')) return

    try {
      await jsonFetch(`/admin/page_contents/${pageContentId}/events/${event.id}`, {
        method: 'DELETE'
      })
      setEvents(prev => prev.filter((_, i) => i !== index))
      showToast('Événement supprimé')
    } catch {
      showToast('Erreur lors de la suppression', false)
    }
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar size={18} className="text-tessan-green" />
          <h2 className="text-lg font-semibold text-text-dark">
            Liste des événements ({events.length})
          </h2>
        </div>
        <Button onClick={addEvent} className="flex items-center gap-2">
          <Plus size={16} />
          Ajouter un événement
        </Button>
      </div>

      <p className="text-sm text-text-gray mb-4">
        Renseigne ici les détails (date + description) de chaque événement affiché dans la page.
        Ces données sont exposées via l'API. Le HTML composé ci-dessus reste pour l'affichage visuel.
      </p>

      {events.length === 0 ? (
        <div className="text-center py-8 text-text-gray bg-gray-50 rounded-lg">
          Aucun événement. Clique sur "Ajouter un événement" pour commencer.
        </div>
      ) : (
        <div className="space-y-3">
          {events.map((event, index) => (
            <div
              key={event.id ?? `new-${index}`}
              className="p-4 border border-gray-200 rounded-lg bg-gray-50"
            >
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                <div className="md:col-span-3">
                  <label className="block text-xs font-medium text-text-dark mb-1">
                    Date *
                  </label>
                  <Input
                    type="date"
                    value={event.event_date}
                    onChange={(e) => updateEvent(index, { event_date: e.target.value, dirty: true })}
                    required
                  />
                </div>
                <div className="md:col-span-9">
                  <label className="block text-xs font-medium text-text-dark mb-1">
                    Description *
                  </label>
                  <Textarea
                    value={event.description}
                    onChange={(e) => updateEvent(index, { description: e.target.value, dirty: true })}
                    rows={2}
                    placeholder="Lieu, horaires, programme..."
                    required
                  />
                </div>
              </div>

              {event.error && (
                <p className="mt-2 text-sm text-error">{event.error}</p>
              )}

              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs text-text-gray">
                  {event.id ? `#${event.id}` : 'Nouveau (non sauvegardé)'}
                  {event.dirty && event.id && <span className="ml-2 text-amber-600">• modifications non sauvegardées</span>}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => saveEvent(index)}
                    disabled={event.saving || !event.event_date || !event.description || (!event.dirty && !!event.id)}
                    className="flex items-center gap-2"
                  >
                    <Save size={14} />
                    {event.saving ? '...' : 'Enregistrer'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => deleteEvent(index)}
                    className="flex items-center gap-2 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}
